<script setup lang="ts">
import type { NotificationSettings } from '@/services/notification.service'

const notificationStore = useNotificationStore()

const form = ref<NotificationSettings>({
    stilleStundenAktiv: false,
    stilleStundenVon: '22:00',
    stilleStundenBis: '07:00',
    pushAbmeldungAktiv: true
})

const message = ref<{ kind: 'success' | 'error'; text: string } | null>(null)

const isLoading = computed(() => notificationStore.isMethodLoading('fetchSettings'))
const isSaving = computed(() => notificationStore.isMethodLoading('saveSettings'))
const isTesting = computed(() => notificationStore.isMethodLoading('sendTestPush'))

const timeRules = [
    (v: string) => /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(v) || 'Format HH:mm (z.B. 22:00)'
]

onMounted(async () => {
    try {
        const fetched = await notificationStore.fetchSettings()
        form.value = { ...fetched }
    } catch (e) {
        console.error(e)
        message.value = { kind: 'error', text: 'Einstellungen konnten nicht geladen werden.' }
    }
})

async function save() {
    message.value = null
    try {
        const saved = await notificationStore.saveSettings({ ...form.value })
        form.value = { ...saved }
        message.value = { kind: 'success', text: 'Einstellungen gespeichert.' }
    } catch (e) {
        console.error(e)
        message.value = { kind: 'error', text: 'Speichern fehlgeschlagen.' }
    }
}

async function testPush() {
    message.value = null
    try {
        await notificationStore.sendTestPush()
        message.value = {
            kind: 'success',
            text: 'Test-Push gesendet. Wenn du nichts siehst, prüfe die Browser-Berechtigungen oben in der Karte „Push-Benachrichtigungen".'
        }
    } catch (e) {
        console.error(e)
        message.value = { kind: 'error', text: 'Test-Push fehlgeschlagen.' }
    }
}
</script>

<template>
    <v-card class="mx-auto mt-6" elevation="2">
        <v-card-title class="text-h6 font-semibold">Welche Pushes möchtest du?</v-card-title>

        <v-card-subtitle class="text-body-2 mb-2">
            Schalte Event-Typen einzeln an oder aus und richte stille Stunden ein.
        </v-card-subtitle>

        <v-divider class="mb-4" />

        <v-card-text v-if="isLoading" class="text-center">
            <v-progress-circular indeterminate />
        </v-card-text>

        <v-card-text v-else>
            <v-list density="compact" class="bg-transparent">
                <v-list-subheader>Event-Typen</v-list-subheader>
                <v-list-item>
                    <template #prepend>
                        <v-icon icon="mdi-account-arrow-right" />
                    </template>
                    <v-list-item-title>Abmeldung im Hort</v-list-item-title>
                    <v-list-item-subtitle>
                        Push, sobald dein Kind als abgemeldet markiert wurde.
                    </v-list-item-subtitle>
                    <template #append>
                        <v-switch
                            v-model="form.pushAbmeldungAktiv"
                            color="primary"
                            hide-details
                            inset
                        />
                    </template>
                </v-list-item>
            </v-list>

            <v-divider class="my-4" />

            <v-list density="compact" class="bg-transparent">
                <v-list-subheader>Stille Stunden</v-list-subheader>
                <v-list-item>
                    <template #prepend>
                        <v-icon icon="mdi-bell-sleep" />
                    </template>
                    <v-list-item-title>Keine Pushes in einem Zeitfenster</v-list-item-title>
                    <v-list-item-subtitle>
                        Pushes werden in der Inbox abgelegt, aber nicht akustisch zugestellt.
                    </v-list-item-subtitle>
                    <template #append>
                        <v-switch
                            v-model="form.stilleStundenAktiv"
                            color="primary"
                            hide-details
                            inset
                        />
                    </template>
                </v-list-item>
            </v-list>

            <v-expand-transition>
                <div v-if="form.stilleStundenAktiv" class="d-flex ga-4 mt-2 pl-4">
                    <v-text-field
                        v-model="form.stilleStundenVon"
                        label="Von"
                        type="time"
                        variant="outlined"
                        density="compact"
                        :rules="timeRules"
                        style="max-width: 160px"
                    />
                    <v-text-field
                        v-model="form.stilleStundenBis"
                        label="Bis"
                        type="time"
                        variant="outlined"
                        density="compact"
                        :rules="timeRules"
                        style="max-width: 160px"
                    />
                </div>
            </v-expand-transition>

            <v-slide-y-transition>
                <v-alert
                    v-if="message"
                    class="mt-4"
                    :type="message.kind"
                    variant="tonal"
                    closable
                    @click:close="message = null"
                >
                    {{ message.text }}
                </v-alert>
            </v-slide-y-transition>
        </v-card-text>

        <v-card-actions class="px-4 pb-4 pt-0 flex-wrap ga-2">
            <v-btn
                color="primary"
                variant="outlined"
                :loading="isTesting"
                :disabled="isSaving"
                prepend-icon="mdi-bell-ring"
                @click="testPush"
            >
                Test-Push senden
            </v-btn>
            <v-spacer />
            <v-btn
                color="primary"
                :loading="isSaving"
                :disabled="isTesting"
                @click="save"
            >
                Einstellungen speichern
            </v-btn>
        </v-card-actions>
    </v-card>
</template>
